/**
 * Global Modal IDs Registry
 * 
 * Centralized registry of all modal IDs used throughout the application.
 * These IDs are used for:
 * - Proper z-index stacking via Mantine Modal.Stack
 * - URL synchronization (when enabled)
 * - Modal state management
 * 
 * 🚨 CRITICAL: All modals that need proper z-index stacking MUST:
 * 1. Be added to MODAL_IDS object
 * 2. Use ResponsiveModal with globalModalId (NOT Dialog)
 * 3. See MODAL_STACKING_FIX.md for complete guide
 */

// Organized modal IDs by feature/entity for better maintainability and type safety
export const MODAL_IDS = {
  // Calendar View Modals (low z-index - background modals)
  calendar: {
    viewContacts: 'calendar-view-contacts',
    viewTickets: 'calendar-view-tickets',
    viewTasks: 'calendar-view-tasks',
    viewTimeEntries: 'calendar-view-time_entries',
    viewCompanies: 'calendar-view-companies',
    viewCommunications: 'calendar-view-communications',
  },

  // Contacts
  contacts: {
    create: 'create-contacts',
    edit: 'edit-contacts',
    delete: 'delete-contacts',
    detail: 'contacts-detail',
    createQuick: 'create-contact',
    consolidate: 'consolidate-contact',
    linkCompany: 'link-company',
  },

  // Tickets
  tickets: {
    create: 'create-tickets',
    edit: 'edit-tickets',
    delete: 'delete-tickets',
    detail: 'tickets-detail',
    createQuick: 'create-ticket',
    quickCreation: 'quick-ticket-creation',
    saveTemplate: 'save-ticket-template',
    editTemplate: 'edit-ticket-template',
    sendCustomerEmail: 'send-ticket-customer-email',
  },

  // Tasks
  tasks: {
    create: 'create-tasks',
    edit: 'edit-tasks',
    delete: 'delete-tasks',
    detail: 'tasks-detail',
    editTask: 'edit-task',
    addNote: 'add-note',
    taskAddNote: 'task-add-note',
    addUpdate: 'add-update',
    followUpTask: 'follow-up-task-modal',
    saveTemplate: 'save-task-template',
    editTemplate: 'edit-task-template',
  },

  // Time Entries
  timeEntries: {
    edit: 'edit-time_entries',
    delete: 'delete-time_entries',
    detail: 'time_entries-detail',
    createQuick: 'create-time-entry',
    createClientChecklist: 'create-client-checklist',
    createTelecomChecklist: 'create-telecom-checklist',
    quickbooksSyncMonthly: 'quickbooks-sync-monthly',
    // Reassign the parent ticket's company/contact (opened from a ticket OR
    // time-entry row). Moves the whole ticket; all its time entries follow.
    reassignCompany: 'reassign-company-contact',
  },

  // Companies
  companies: {
    create: 'create-companies',
    edit: 'edit-companies',
    delete: 'delete-companies',
    detail: 'companies-detail',
    consolidate: 'consolidate-company',
    research: 'research-company',
    findDecisionMakers: 'find-decision-makers',
    linkContact: 'link-contact',
    changeDriveFolder: 'change-company-drive-folder',
    enrollSupportGroup: 'enroll-support-group',
  },

  // Communications
  communications: {
    create: 'create-communications',
    edit: 'edit-communications',
    delete: 'delete-communications',
    detail: 'communications-detail',
    reassignSalesAttribution: 'reassign-communication-sales-attribution',
  },

  // Employees
  employees: {
    create: 'create-employees',
    edit: 'edit-employees',
    delete: 'delete-employees',
    detail: 'employees-detail',
    createUser: 'create-user',
    editUser: 'edit-user',
    deleteUser: 'delete-user',
    grantAccess: 'grant-employee-access',
  },

  // Users (admin users table)
  users: {
    // Must match `${entityName}-detail` for the admin_users entity — that is
    // the id `UniversalEntityPage` builds on a row click and the id
    // `UniversalDetailModalContainer` is mounted under. A separate
    // 'users-detail' spelling existed here for a while and matched nothing,
    // which is why clicking a user's name did nothing at all.
    detail: 'admin_users-detail',
    // User-centric access grants: a USER (employee or client) is granted access
    // to one or more CONTACTS. Distinct from employees.grantAccess, which is the
    // CONTACT-centric flow (a single contact shared with employees).
    grantClientAccess: 'user-grant-client-access',
    grantEmployeeAccess: 'user-grant-employee-access',
    // Lend this admin's Google Drive OAuth connection to a Drive-less admin.
    grantDriveAccess: 'user-grant-drive-access',
  },

  // Deals
  deals: {
    create: 'create-deals',
    edit: 'edit-deals',
    delete: 'delete-deals',
    detail: 'deals-detail',
    createQuick: 'create-deal',
    changeStage: 'change-deal-stage',
    markWon: 'mark-deal-won',
    markLost: 'mark-deal-lost',
    convertOffering: 'convert-offering',
    snooze: 'snooze-deal',
    scheduleFollowUp: 'schedule-deal-followup',
    requestQuote: 'request-deal-quote',
    makeProjectChannel: 'make-project-channel',
    createInvoice: 'create-deal-invoice',
    createQuote: 'create-deal-quote',
  },

  // Contracts (e-signature)
  contracts: {
    detail: 'contracts-detail',
    withdraw: 'withdraw-contract',
    edit: 'edit-contract',
  },

  // Social Media Posts (marketing)
  socialMediaPosts: {
    detail: 'social_media_posts-detail',
    publishConfirm: 'social-post-publish-confirm',
  },

  // Messages & Communications
  messages: {
    detail: 'message-detail',
    compose: 'compose-message',
    composeEmail: 'compose-email',
    chooseTemplate: 'choose-template',
    logActivity: 'log-activity',
    bulkEmail: 'bulk-email',
    shareDocument: 'share-document',
    shareSignature: 'share-signature',
    shareTemplate: 'share-template',
    shareConnection: 'share-connection',
    removeConnection: 'remove-connection',
    changeStatus: 'change-status',
    composeHistory: 'compose-history',
    sendTemplate: 'send-template',
    sendText: 'send-text-message',
    sendToSlack: 'send-to-slack',
    saveAttachmentsToGoogleDrive: 'save-attachments-to-google-drive',
    shareAttachmentsToSlack: 'share-attachments-to-slack',
    exportThreadToSlack: 'export-email-thread-to-slack',
  },

  // Offerings
  offerings: {
    create: 'create-offerings',
    detail: 'offerings-detail',
    convertToDeal: 'convert-offering-to-deal',
    solutionEngineering: 'solution-engineering',
  },

  // Recurring Transactions
  recurringTransactions: {
    detail: 'recurring-transactions-detail',
  },

  // Recurring Charges
  recurringCharges: {
    detail: 'recurring-charges-detail',
    chargeCardOnFile: 'charge-card-on-file',
    changeSchedule: 'change-schedule',
  },

  // Email Campaigns
  emailCampaigns: {
    detail: 'email_campaigns-detail',
  },

  // Meetings
  meetings: {
    create: 'create-meeting',
  },

  // Due Date (entity-agnostic — any entity with a due_date field)
  dueDate: {
    set: 'set-due-date',
  },

  // Import/Export
  importExport: {
    csvImport: 'csv-import',
    bulkExport: 'bulk-export',
    bulkActionsProgress: 'bulk-actions-progress',
    bulkExportEmailsContacts: 'bulk-export-emails-contacts',
    bulkExportEmailsCompanies: 'bulk-export-emails-companies',
    bulkExportEmailsDeals: 'bulk-export-emails-deals',
  },

  // Confirmation Modals
  confirmation: {
    delete: 'delete-confirmation',
    bulkDelete: 'bulk-delete-confirmation',
  },

  // File Management
  files: {
    googleDrivePicker: 'google-drive-picker',
    fileUpload: 'file-upload',
  },

  // Search and Navigation
  navigation: {
    globalSearch: 'global-search',
    mobile: 'mobile-navigation',
    mobileDetail: 'mobile-detail-modal',
    mobileRowActions: 'mobile-row-actions-modal',
    domainSearch: 'domain-search',
  },

  // Sales sequences "Today" workspace
  today: {
    actionDrawer: 'today-action-drawer',
    enrollmentDrawer: 'sequence-enrollment-drawer',
  },

  // Settings
  settings: {
    account: 'account-settings',
    preferences: 'preferences-settings',
  },

  // JSON Response
  jsonResponse: {
    success: 'json-response',
    error: 'json-response-error',
  },

  // JSON Field Editors
  jsonEditors: {
    socialLinks: 'json-editor-social_links',
    customFields: 'json-editor-custom_fields',
    currentTechnologies: 'json-editor-current_technologies',
    employeeSize: 'json-editor-employee_size',
  },

  // List Field Editors (tags, etc.)
  listEditors: {
    contactsTags: 'list-editor-contacts-tags',
    companiesTags: 'list-editor-companies-tags',
    ticketsTags: 'list-editor-tickets-tags',
    tasksTags: 'list-editor-tasks-tags',
    timeEntriesTags: 'list-editor-time_entries-tags',
    employeesTags: 'list-editor-employees-tags',
    communicationsTags: 'list-editor-communications-tags',
    dealsTags: 'list-editor-deals-tags',
  },

  // AI/Chat
  ai: {
    askMeridianEntity: 'ask-meridian-entity',
    chatSettings: 'chat-settings',
    chatCapabilities: 'chat-capabilities',
    chatTokenUsage: 'chat-token-usage',
    chatSkills: 'chat-skills',
  },

  // CEO Dashboard
  ceoDashboard: {
    sendEmailDeal: 'ceo-send-email-deal',
    logActivity: 'ceo-log-activity',
    assignOwner: 'ceo-assign-owner',
    bulkEmail: 'ceo-bulk-email',
  },

  // Resource Allocation
  resourceAllocation: {
    drillDown: 'allocate-drill-down',
    assignDeal: 'allocate-assign-deal',
    assignTicket: 'allocate-assign-ticket',
  },

  // Automation
  automation: {
    configureAutomatedTasks: 'configure-automated-tasks',
    configureCommunicationsGoals: 'configure-communications-goals',
  },

  // Universal/Generic
  universal: {
    edit: 'edit',
    manageTableColumns: 'manage-table-columns',
  },

  // Cross-Entity Action Resolver
  actions: {
    resolver: 'action-resolver',
  },

  // Entity History & Audit (append-only S3 audit log)
  // The two history modals are PLACES — they are also registered in hub's
  // MODAL_URL_CONFIG so Back closes / Forward re-opens them. `restoreConfirm`
  // is a MOMENT: registered here (so it stacks above the history modal) but
  // deliberately absent from MODAL_URL_CONFIG, because Forward could not
  // faithfully restore a half-confirmed write.
  audit: {
    contactsHistory: 'contacts-audit-history',
    companiesHistory: 'companies-audit-history',
    restoreConfirm: 'restore-audit-change',
  },
} as const;

// Flatten for backward compatibility with Mantine's useModalsStack.
// EVERY modal that should be openable via URL (?modal=<id>) or via
// useGlobalModalStack() MUST have its ID present in this array.
export const GLOBAL_MODAL_IDS: string[] = [
  // Calendar View Modals (low z-index - background modals)
  'calendar-view-contacts',
  'calendar-view-tickets',
  'calendar-view-tasks',
  'calendar-view-time_entries',
  'calendar-view-companies',
  'calendar-view-communications',

  // Universal Entity Modals
  'create-contacts',
  'edit-contacts',
  'delete-contacts',
  'contacts-detail',
  'create-tickets',
  'edit-tickets',
  'delete-tickets',
  'tickets-detail',
  'send-ticket-customer-email',
  'create-tasks',
  'edit-tasks',
  'delete-tasks',
  'tasks-detail',
  'edit-time_entries',
  'delete-time_entries',
  'time_entries-detail',
  'reassign-company-contact',
  'create-companies',
  'edit-companies',
  'delete-companies',
  'companies-detail',
  'change-company-drive-folder',
  'create-communications',
  'edit-communications',
  'delete-communications',
  'communications-detail',
  'reassign-communication-sales-attribution',
  'create-employees',
  'edit-employees',
  'delete-employees',
  'employees-detail',
  'admin_users-detail',
  'create-admin_users',
  'edit-admin_users',
  'delete-admin_users',
  'create-deals',
  'edit-deals',
  'delete-deals',
  'deals-detail',

  // Universal Detail Page Modal
  'edit',

  // Template Modals
  'save-ticket-template',
  'save-task-template',
  'edit-ticket-template',
  'edit-task-template',

  // Import/Export Modals
  'csv-import',
  'bulk-export',
  'bulk-actions-progress',
  'bulk-export-emails-contacts',
  'bulk-export-emails-companies',
  'bulk-export-emails-deals',

  // User Management Modals
  'create-user',
  'edit-user',
  'delete-user',
  'grant-employee-access',
  'user-grant-client-access',
  'user-grant-employee-access',
  'consolidate-contact',
  'consolidate-company',
  'enroll-support-group',

  // CRM Modals
  'compose-message',
  'compose-email',
  'choose-template',
  'log-activity',
  'bulk-email',
  'share-document',
  'share-signature',
  'share-template',
  'share-connection',
  'remove-connection',
  'change-status',
  'compose-history',
  'send-template',
  'send-text-message',
  'send-to-slack',
  'save-attachments-to-google-drive',
  'share-attachments-to-slack',
  'export-email-thread-to-slack',
  'message-detail',

  // Deal Modals
  'change-deal-stage',
  'mark-deal-won',
  'mark-deal-lost',
  'snooze-deal',
  'schedule-deal-followup',
  'request-deal-quote',
  'make-project-channel',

  // Contracts (e-signature)
  'contracts-detail',
  'withdraw-contract',
  'edit-contract',

  // Social Media Posts (marketing)
  'social_media_posts-detail',
  'social-post-publish-confirm',

  // Meeting & Task Modals
  'create-meeting',
  'add-note',
  'task-add-note',
  'add-update',
  'follow-up-task-modal',

  // Due Date (entity-agnostic)
  'set-due-date',

  // Cross-Entity Creation Modals
  'create-contact',
  'create-ticket',
  'create-time-entry',
  'create-deal',
  'create-offerings',
  'offerings-detail',
  'convert-offering-to-deal',
  'solution-engineering',
  'create-client-checklist',
  'create-telecom-checklist',
  'quickbooks-sync-monthly',

  // Quick Actions Modals
  'quick-ticket-creation',
  'quick-deal-creation',
  'quick-time-entry',

  // Modal Variants (different naming conventions)
  'create-contact-modal',
  'create-ticket-modal',
  'edit-task',
  'mobile-detail-modal',
  'mobile-row-actions-modal',
  'domain-search',

  // Confirmation Modals
  'delete-confirmation',
  'bulk-delete-confirmation',

  // File Management Modals
  'google-drive-picker',
  'file-upload',

  // Search and Navigation Modals
  'global-search',
  'mobile-navigation',

  // Sales sequences "Today" workspace
  'today-action-drawer',
  'sequence-enrollment-drawer',

  // Settings Modals
  'account-settings',
  'preferences-settings',

  // JSON Response Modals
  'json-response',
  'json-response-error',

  // JSON Field Editor Modals (dynamic IDs: json-editor-{fieldId})
  // These are registered dynamically, but we include a pattern match
  'json-editor-social_links',
  'json-editor-custom_fields',
  'json-editor-current_technologies',
  'json-editor-employee_size',

  // List Field Editor Modals (dynamic IDs: list-editor-{entityName}-{entityId}-{fieldId})
  // These are registered dynamically for tags and other list fields
  // Pattern: list-editor-{entityName}-{entityId}-{fieldId}
  // Examples: list-editor-contacts-abc123-tags, list-editor-companies-xyz789-tags
  'list-editor-contacts-tags',
  'list-editor-companies-tags',
  'list-editor-tickets-tags',
  'list-editor-tasks-tags',
  'list-editor-time_entries-tags',
  'list-editor-employees-tags',
  'list-editor-communications-tags',
  'list-editor-deals-tags',

  // Entity-Focused Chat Modal
  'ask-meridian-entity',

  // Meridian chat chrome modals
  'chat-settings',
  'chat-capabilities',
  'chat-token-usage',
  'chat-skills',

  // Research Company Modals
  'research-company',
  'find-decision-makers',

  // CEO Dashboard Action Modals
  'ceo-send-email-deal',
  'ceo-log-activity',
  'ceo-assign-owner',
  'ceo-bulk-email',

  // Resource Allocation Modals
  'allocate-drill-down',
  'allocate-assign-deal',
  'allocate-assign-ticket',

  // Automation Modals
  'configure-automated-tasks',
  'configure-communications-goals',

  // Recurring Transactions Modals
  'recurring-transactions-detail',
  'recurring-charges-detail',
  'email_campaigns-detail',
  'charge-card-on-file',
  'change-schedule',

  // Nested over entity detail modals (column picker, etc.)
  'manage-table-columns',

  // Cross-Entity Action Resolver
  'action-resolver',

  // Entity History & Audit. The two history modals are URL-registered places;
  // 'restore-audit-change' is a stack-only moment (no history entry).
  'contacts-audit-history',
  'companies-audit-history',
  'restore-audit-change',
];

// Type-safe modal ID type
export type ModalId = typeof GLOBAL_MODAL_IDS[number];
